import $ from 'jquery';
import { diffLines } from 'diff';
import { CODEX_LINE_HEIGHT } from 'app/codex/constants';

/**
 * @typedef {Object} DeemphasizeInfoItem
 * @property {Number} start Starting line of the de-emphasized section
 * @property {Number} end Ending line of the de-emphasized section
 * @property {Boolean} prevent_click Prevent clicking a section to edit the code
 */

/**
 * @typedef {Object} DiffPart
 * @property {String} value The code for this part of the diff
 * @property {Boolean} [added] True if this section was added to the original code
 * @property {Boolean} [removed] True if this section was removed from the original code
 */

/**
 * Copy an array of DeemphasizeInfoItem objects
 * @param {Array.<DeemphasizeInfoItem>} deemphasizeInfo
 */
function copyDeemphasizeInfo(deemphasizeInfo) {
  return deemphasizeInfo.map(info => Object.assign({}, info));
}

/**
 * Compare two code blocks and use the diff to update the position of the deemphasized code
 * sections.
 * @param {object} opts
 * @param {Array.<DeemphasizeInfoItem>} opts.deemphasizeInfo
 * @param {String} opts.initial Initial/unchanged code
 * @param {String} opts.current Current/changed code
 * @returns {Array.<DeemphasizeInfoItem>}
 */
function recalculateDeemphasizeInfo({ deemphasizeInfo, initial, current }) {
  const mutableDeemphasizeInfo = copyDeemphasizeInfo(deemphasizeInfo);
  let linePosition = 0;
  let previousNumLines = 0;

  // This creates an array of diff sections for the combined code block. See the DiffPart
  // typedef above for more details.
  const diffParts = diffLines(initial, current);

  diffParts.forEach((diffPart, index) => {
    let numLines = diffPart.value.split('\n').length - 1;

    // Removed lines should be subtracted
    if (diffPart.removed) {
      numLines *= -1;
    }

    // When code is changed (i.e. lines weren't just added or removed), the diff will have one
    // or more lines removed, then one or more lines added. In this case, we need to skip the
    // removed section and subtract those lines in the next iteration.
    if (diffPart.added) {
      numLines += previousNumLines;
    } else if (diffPart.removed) {
      if (diffParts[index + 1] && diffParts[index + 1].added) {
        previousNumLines = numLines;
        return;
      }
    }

    // If something changed, recalculate deemphasizeInfo. If a start or end point is after the
    // current line position, that point needs to be moved up or down by numLines.
    if (diffPart.added || diffPart.removed) {
      mutableDeemphasizeInfo.forEach(info => {
        if (info.start >= linePosition) {
          info.start += numLines;
        }
        if (info.end >= linePosition) {
          info.end += numLines;
        }
      });
    }

    // Update the line position and reset previousNumLines
    linePosition += numLines - previousNumLines;
    previousNumLines = 0;
  });

  return mutableDeemphasizeInfo;
}

/**
 * Render de-emphasize sections on the code
 * @param {Object} opts
 * @param {CodeMirror} opts.cm
 * @param {Array.<JQuery>} opts.existingSections
 * @param {Array.<DeemphsizeInfoItem>} opts.deemphasizeInfo
 * @param {String} opts.className
 */
function renderDeemphasizeSections({ cm, existingSections, deemphasizeInfo, className }) {
  let existingSectionsLocal = existingSections.slice(0, existingSections.length);
  existingSectionsLocal.forEach($section => $section.remove());
  existingSectionsLocal = [];

  if (deemphasizeInfo.length) {
    const scrollIntoView = false;
    const $deemphasizeBase = $('<div/>');
    $deemphasizeBase.addClass(className);
    $deemphasizeBase.css({ height: CODEX_LINE_HEIGHT });

    deemphasizeInfo.forEach((info, index) => {
      let counter = info.start;
      while (counter <= info.end) {
        const $deemphasize = $deemphasizeBase.clone();
        $deemphasize.attr('data-index', index);
        $deemphasize.css({ marginTop: -CODEX_LINE_HEIGHT });

        cm.addWidget(
          {
            line: counter,
            ch: 0,
          },
          $deemphasize[0],
          scrollIntoView,
        );

        if (info.prevent_click) {
          $deemphasize.addClass('prevent-click');
        }

        existingSectionsLocal.push($deemphasize);
        counter++;
      }
    });
  }

  return existingSectionsLocal;
}

/**
 * Thumbs up
 * @type {String}
 */
const EMOJI_THUMBSUP = '&#128077;';

/**
 * Frown
 * @tpye {String}
 */
const EMOJI_FROWNYFACE = '&#128577;';

/** Templates for the admin form */
const templates = {
  /**
   * Template for the form table header
   * @returns {String}
   */
  tableHeader: () => `
        <thead>
            <tr>
                <th>Line start</th>
                <th>Line end</th>
                <th>Prevent click?</th>
                <th>Valid?</th>
                <th>&nbsp;</th>
            </tr>
        </thead>
    `,

  /**
   * Template for an individual de-emphasized section form field
   * @param {Object} opts
   * @param {Number} opts.index
   * @param {DeemphasizeInfoItem} opts.data
   * @param {Number} opts.numLines
   * @returns {String}
   */
  field: ({ index, data, numLines }) => `
        <tr data-index="${index}">
            <td class="number">
                <input type="number"
                    min="1"
                    max="${numLines}"
                    value="${data.start}"
                    data-name="start">
            </td>
            <td class="number">
                <input type="number"
                    min="1"
                    max="${numLines}"
                    value="${data.end}"
                    data-name="end">
            </td>
            <td class="checkbox">
                <input type="checkbox"
                    ${data.prevent_click ? 'checked' : ''}
                    data-name="prevent_click">
            </td>
            <td class="error">
                ${data.start > 0 && data.end > 0 ? EMOJI_THUMBSUP : '&hellip;'}
            </td>
            <td class="remove">
                <span class="remove-row css-sprite-global" data-index="${index}"></span>
            </td>
        </tr>
    `,

  /**
   * Template for a button to add a new de-emphasize field to the form
   * @returns {String}
   */
  addButton: () => '<span class="btn add-row">Add de-emphasized section</span>',
};

export class CodexDeemphasizerEditor {
  /**
   * Dynamic form for manipulating de-emphasize JSON data and previewing the results
   * @param {object} opts
   * @param {CodeMirror} opts.cm
   * @param {JQuery} $deemphasizeField Textarea that holds the de-emphasize JSON
   * @param {JQuery} $deemphasizeContainer Container for the dynamic form fields
   */
  constructor({ cm, $deemphasizeField, $deemphasizeContainer }) {
    this.cm = cm;
    this.$deemphasizeField = $deemphasizeField;
    this.$deemphasizeContainer = $deemphasizeContainer;
    this.initialCode = this.cm.getValue();
    this.existingSections = [];

    this.readData();
    this.renderFields();
    this.renderCodexAnnotations();
    this.bindCodeMirrorEvents();
  }

  /** Parse the JSON from the de-emphasize textarea */
  readData() {
    try {
      this.data = JSON.parse(this.$deemphasizeField.val());
    } catch (e) {
      this.data = {};
    }

    this.deemphasizeInfo = this.data.deemphasizeInfo || [];
  }

  /** Write local data to the de-emphasize textarea */
  saveData() {
    const validatedInfo = this.deemphasizeInfo
      .filter(info => this.validateInfoItem(info).success)
      .map(info => ({
        prevent_click: info.prevent_click === true,
        ...info,
      }));

    const data = {};
    if (validatedInfo.length) {
      data.deemphasizeInfo = validatedInfo;
    }
    this.$deemphasizeField.val(JSON.stringify(data, null, 2));
  }

  /** Render form fields from the JSON data */
  renderFields() {
    this.$deemphasizeContainer.empty();
    if (this.deemphasizeInfo.length) {
      const $el = $('<table/>');
      const $tbody = $('<tbody/>');

      $el.append(templates.tableHeader());
      $el.append($tbody);

      this.deemphasizeInfo.forEach((info, index) => {
        $tbody.append(
          templates.field({
            index,
            data: {
              start: info.start === '' ? '' : info.start + 1,
              end: info.end === '' ? '' : info.end + 1,
              prevent_click: info.prevent_click,
            },
            numLines: this.getNumLines(),
          }),
        );
      });

      this.$deemphasizeContainer.append($el);
    }

    this.$deemphasizeContainer.append(templates.addButton());
    this.bindFieldsEvents();
  }

  /** Render the de-emphasized sections on the code */
  renderCodexAnnotations() {
    this.existingSections = renderDeemphasizeSections({
      cm: this.cm,
      existingSections: this.existingSections,
      deemphasizeInfo: this.deemphasizeInfo,
      className: 'codex-deemphasize-highlight-admin',
    });
  }

  /** Add events for interacting with the dynamic form fields */
  bindFieldsEvents() {
    // Add a new input row
    this.$deemphasizeContainer.find('.add-row').on('click', () => {
      this.deemphasizeInfo.push({
        start: '',
        end: '',
        prevent_click: false,
      });
      this.renderFields();
    });

    // Remove an input row
    this.$deemphasizeContainer.find('.remove-row').on('click', e => {
      this.deemphasizeInfo.splice($(e.target).data('index'), 1);
      this.renderFields();
      this.renderCodexAnnotations();
      this.saveData();
    });

    // Edit an input row
    this.$deemphasizeContainer.find('input').on('input', e => {
      const $targetInput = $(e.target);
      const $row = $targetInput.parents('tr');
      const $inputs = $row.find('input');
      const index = $row.data('index');
      const info = {};

      $inputs.each((_, el) => {
        const $input = $(el);
        const inputType = $input.prop('type');
        let value;
        switch (inputType) {
          case 'number':
            // minus 1 because lines are 0-indexed
            value = parseInt($input.val(), 10) - 1;
            if (Number.isNaN(value)) {
              value = null;
            }
            break;
          case 'checkbox':
            value = $input.prop('checked');
            break;
          default:
            throw new Error(`Unexpected input type: ${inputType}`);
        }

        info[$input.data('name')] = value;
      });

      const validated = this.validateInfoItem(info);
      const $error = $row.find('.error');
      if (validated.success) {
        $error.html(EMOJI_THUMBSUP);
        this.deemphasizeInfo[index] = info;
        this.renderCodexAnnotations();
        this.saveData();
      } else {
        $error.html(`${EMOJI_FROWNYFACE} ${validated.error}`);
      }
    });
  }

  /** Add events for updating the data and form when editing code */
  bindCodeMirrorEvents() {
    this.cm.on('change', () => {
      const currentCode = this.cm.getValue();
      this.deemphasizeInfo = recalculateDeemphasizeInfo({
        deemphasizeInfo: this.deemphasizeInfo,
        initial: this.initialCode,
        current: currentCode,
      });

      // Reset initial code once we've calculated the new deemphasizeInfo
      this.initialCode = currentCode;

      this.renderFields();
      this.renderCodexAnnotations();
      this.saveData();
    });
  }

  /**
   * Get the current number of lines in the code
   * @returns {Number}
   */
  getNumLines() {
    return this.cm.getValue().split('\n').length;
  }

  /**
   * Validate a row of de-emphasize data and return either success or error
   * @param {DeemphasizeInfoItem} infoItem
   * @returns {{success: Boolean, error: String}}
   */
  validateInfoItem(infoItem) {
    const numLines = this.getNumLines();
    let success = false;
    let error = null;

    if (typeof infoItem.start !== 'number') {
      error = 'Start is not a number';
    } else if (typeof infoItem.end !== 'number') {
      error = 'End is not a number';
    } else if (infoItem.start < 0 || infoItem.end < 0) {
      error = 'Start and end must be greater than 0';
    } else if (infoItem.start >= numLines || infoItem.end >= numLines) {
      error = `Start and end must be less than ${numLines}`;
    } else if (infoItem.start > infoItem.end) {
      error = 'Start is greater than end';
    } else {
      success = true;
    }

    return { success, error };
  }
}

/**
 * @typedef ExistingDeemphasizeData
 * @property {Array.<DeemphasizeInfoItem>} initial Initial deemphasize data
 * @property {Array.<DeemphasizeInfoItem>} current Current deemphasize data
 */

/**
 * Map of codex envs to existing de-emphasize data
 * @type {Object.<Number, ExistingDeemphasizeData>}
 */
const deemphasizeInfoByCodexEnv = {};

export class CodexDeemphasizer {
  /**
   * Handle showing de-emphasized sections of code on a user-facing codex instance
   * @param {Object} opts
   * @param {CodeMirror} opts.cm
   * @param {Object} opts.data
   * @param {Array.<DeemphasizeInfoItem>} opts.data.deemphasizeInfo
   * @param {String} opts.initialCode
   */
  constructor({ cm, data, envId, initialCode }) {
    this.cm = cm;
    this.envId = envId;

    const existingData = deemphasizeInfoByCodexEnv[envId] || {};
    this.deemphasizeInfo = existingData.current || data.deemphasizeInfo || [];
    this.initialDeemphasizeInfo = existingData.initial || copyDeemphasizeInfo(this.deemphasizeInfo);
    this.initialCode = initialCode;

    this.existingSections = [];
    this.deemphasizeClassName = 'codex-deemphasize-highlight';

    this.updateData();

    this.render();
    this.bindCodeMirrorEvents();
    this.bindDeemphasizeEvents();
  }

  /** Render de-emphasized sections of the code */
  render() {
    this.existingSections = renderDeemphasizeSections({
      cm: this.cm,
      existingSections: this.existingSections,
      deemphasizeInfo: this.deemphasizeInfo,
      className: this.deemphasizeClassName,
    });
  }

  /** Add events for editing code in or around de-emphasized sections */
  bindCodeMirrorEvents() {
    // Update de-emphasized sections when editing code
    this.cm.on('change', () => {
      this.updateData();
      this.render();
      this.bindDeemphasizeEvents();
    });

    // Prevent user from editing de-emphasized sections
    this.cm.on('beforeChange', (_, change) => {
      // Ignore if this is caused by our code (i.e. clicking the view original code button)
      if (this.cm.brilliant.isEditing) {
        return;
      }
      for (let index = 0; index < this.deemphasizeInfo.length; index++) {
        const info = this.deemphasizeInfo[index];
        if (change.from.line >= info.start && change.from.line <= info.end) {
          change.cancel();
          break;
        }
      }
    });
  }

  /** Add events for interacting with the de-emphasized sections */
  bindDeemphasizeEvents() {
    if (this.existingSections.length) {
      const hoverClass = 'hover';
      const $firstExistingSection = this.existingSections[0];
      const $existingSections = $firstExistingSection.add(
        $firstExistingSection.siblings(`.${this.deemphasizeClassName}`),
      );

      // Each line is an individual "widget," so we have to manually put the whole
      // de-emphasize section into a hover state when hovering on one de-emphasized line.
      $existingSections.on('mouseenter', e => {
        const $target = $(e.target);
        $existingSections.removeClass(hoverClass);
        $existingSections.filter(`[data-index=${$target.data('index')}]`).addClass(hoverClass);
      });
      $existingSections.on('mouseleave', () => $existingSections.removeClass(hoverClass));

      // For clickable sections, remove that section from the data and re-render when
      // clicking
      $existingSections.filter(':not(.prevent-click)').on('click', e => {
        this.removeInfoItem($(e.target).data('index'));
        this.render();
        this.bindDeemphasizeEvents();
      });
    }
  }

  /**
   * Remove the de-emphasize info item at index
   * @param {Number} index
   */
  removeInfoItem(index) {
    this.deemphasizeInfo.splice(index, 1);
    this.initialDeemphasizeInfo.splice(index, 1);
    deemphasizeInfoByCodexEnv[this.envId] = {
      initial: copyDeemphasizeInfo(this.initialDeemphasizeInfo),
      current: copyDeemphasizeInfo(this.deemphasizeInfo),
    };
  }

  /** Recalculate deemphasizeInfo based on current code changes */
  updateData() {
    this.deemphasizeInfo = recalculateDeemphasizeInfo({
      deemphasizeInfo: copyDeemphasizeInfo(this.initialDeemphasizeInfo),
      initial: this.initialCode,
      current: this.cm.getValue(),
    });
  }
}
